import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import RangeTypeSelectBox from 'components/molecules/range-type-select-box';
import BaseDatepicker from 'components/atoms/base-datepicker';
import SmsStatusSelectBox from 'components/molecules/sms-status-select-box';
import SmsTypeSelectBox from 'components/molecules/sms-type-select-box';
import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import { DateFormats, RangeTypes } from 'infrastructure/enums';
import { endOfDate, startOfDate } from 'infrastructure/functions';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import s from './styles.module.scss';

import type {
  BaseRangeDatepickerProps,
  RangePickerValue,
  TOnCalendarChange,
} from 'components/atoms/base-range-datepicker';
import type { ISmsMetricsFilters } from 'infrastructure/interfaces';

interface IProps {
  data: ISmsMetricsFilters;
  updateFilter: (data: ISmsMetricsFilters) => void;
  resetFilter: () => void;
  count: number;
}

const ExpandableSmsMetricsFilters: React.FC<IProps> = (props) => {
  const { data, count, updateFilter, resetFilter } = props;

  const { t } = useTranslation();

  const isCustomRange = data.rangeType === RangeTypes.Custom;
  const isMonthlyRange = data.rangeType === RangeTypes.Monthly;
  const isWeeklyRange = data.rangeType === RangeTypes.Weekly;
  const isDailyRange = data.rangeType === RangeTypes.Daily;

  const pickerType = useMemo<BaseRangeDatepickerProps['picker']>(() => {
    if (isMonthlyRange) return 'month';
    if (isWeeklyRange) return 'week';
    return 'date';
  }, [isMonthlyRange, isWeeklyRange]);

  const dateFormat = useMemo<DateFormats>(() => {
    if (isMonthlyRange) return DateFormats['MMMM yyyy'];
    if (isWeeklyRange) return DateFormats['yyyy-Io'];
    return DateFormats['MM-dd-yyyy'];
  }, [isMonthlyRange, isWeeklyRange]);

  const selectedDate = data.startTime ? new Date(data.startTime) : undefined;

  const selectedRange: RangePickerValue = [
    selectedDate,
    data.endTime ? new Date(data.endTime) : null,
  ];

  const onDateRangeChange: TOnCalendarChange = (dates, _, info, preset) => {
    const [start, end] = dates;

    if (!start && !end) {
      updateFilter({
        startTime: undefined,
        endTime: undefined,
      });
      return;
    }

    if (preset && start && end) {
      updateFilter({
        startTime: startOfDate(start).getTime(),
        endTime: endOfDate(end).getTime(),
      });
      return;
    }

    if (start && info.range === 'start') {
      updateFilter({
        startTime: startOfDate(start).getTime(),
      });
    }
    if (end && info.range === 'end') {
      updateFilter({
        endTime: endOfDate(end).getTime(),
      });
    }
  };

  const onDateChange = useCallback(
    (date: Date) => {
      if (date) {
        if (isMonthlyRange) {
          updateFilter({
            startTime: startOfMonth(date).getTime(),
            endTime: endOfMonth(date).getTime(),
          });
        }
        if (isWeeklyRange) {
          updateFilter({
            startTime: startOfWeek(date).getTime(),
            endTime: endOfWeek(date).getTime(),
          });
        }
        if (isDailyRange) {
          updateFilter({
            startTime: startOfDay(date).getTime(),
            endTime: endOfDay(date).getTime(),
          });
        }
      }
    },
    [isMonthlyRange, isWeeklyRange, isDailyRange],
  );

  const handleReset = () => {
    if (resetFilter) resetFilter();
  };

  useEffect(() => {
    if (selectedDate) {
      onDateChange(selectedDate);
    }
  }, [isCustomRange, isMonthlyRange, isWeeklyRange, isDailyRange]);

  return (
    <ExpandableFilterPanel
      filtersCount={count}
      onReset={handleReset}
      openWithValue
      isExpanded
    >
      <div className={s.form}>
        <ExpandableFilterPanelItem label={t('labels.rangeType')}>
          <RangeTypeSelectBox
            value={data.rangeType}
            onChange={(rangeType) => {
              updateFilter({ rangeType });
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.date')}>
          {isCustomRange ? (
            <BaseRangeDatepicker
              onCalendarChange={onDateRangeChange}
              value={selectedRange}
              withPresets
              isMaxDayToday
              disabledDate={(current) => {
                return current && current > new Date();
              }}
            />
          ) : (
            <BaseDatepicker
              format={dateFormat}
              value={selectedDate}
              onChange={onDateChange}
              picker={pickerType}
              disabledDate={(current) => {
                return current && current > new Date();
              }}
            />
          )}
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.smsStatus')}>
          <SmsStatusSelectBox
            placeholder={t('controls.all')}
            mode="multiple"
            value={data.status}
            withAll
            onChange={(status) => {
              if (Array.isArray(status) && status.includes(null)) {
                updateFilter({ status: undefined });
              } else if (status !== null) {
                updateFilter({ status });
              }
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.smsType')}>
          <SmsTypeSelectBox
            placeholder={t('controls.all')}
            mode="multiple"
            value={data.smsType}
            withAll
            onChange={(smsType) => {
              if (Array.isArray(smsType) && smsType.includes(null)) {
                updateFilter({ status: undefined });
              } else if (smsType !== null) {
                updateFilter({ smsType });
              }
            }}
          />
        </ExpandableFilterPanelItem>
      </div>
    </ExpandableFilterPanel>
  );
};

export default ExpandableSmsMetricsFilters;
